import React from 'react';
import ImageSection from '../components/ImageSection.js';
import quote from '../images/pull-quote.png';
import pyramid from '../images/pyramid.png';
import pyramidMobile from '../images/pyramid-mobile.png';

const copy = [
  {
    section: 0.5,
    content: {
      paragraph: [
        <p>
          Who wants to be a millionaire? Well, lots of people, probably, but that doesn’t mean they’ll be satisfied once it happens.{' '}
          <a href="https://money.cnn.com/2013/07/24/investing/wealthy-investors/index.html" target="_blank" rel="noopener noreferrer">
            After all, 70% of American millionaires don’t consider themselves wealthy
          </a>
          . Make that first million, and it just raises the bar.
        </p>,
        <p>
          It’s time to rethink what it means to be wealthy. And doing that, experts tell us, means understanding our financial psychology and rewiring it for achieving happiness.{' '}
        </p>,
        <p className="bold">Here are four steps to get started.</p>,
      ],
    },
  },
  {
    section: 1,
    content: {
      title: <h3>Step One: Define Your Goal</h3>,
      paragraph: [
        <p>
          Here’s the thing:{' '}
          <a href="https://money.cnn.com/2014/06/05/news/economy/how-much-income-to-be-happy/index.html" target="_blank" rel="noopener noreferrer">
            studies show
          </a>{' '}
          that there <span className="italic">is</span> a financial threshold at which most people are demonstrably more satisfied. But according to financial therapist Lindsay
          Bryan-Podvin, that magic “happiness number”—an income of $75,000 a year—is only one piece of the puzzle.
        </p>,
        <p>
          Digging deeper, Bryan-Podvin says, the key is making your money a tool for achieving happiness rather than the source of it. “What I find to be most beneficial is
          spending, saving and investing in-line with their personal priorities,” she says. “That brings the best return on investment in terms of happiness.”
        </p>,
        <ImageSection selectedImage={quote} mobileImage={quote} classname={'quote'} />,
        <p>
          Bryan-Podvin says this means different things for different people. For one person, that could mean taking an extra hour out of life to prioritize self-care and to relax.
          For another, it could mean putting money toward social activities, or investing to feel a greater sense of security. “It really comes down to what works best for an
          individual, how they want to spend their money, and what is going to make them feel good,” she says.
        </p>,
        <p>
          For many people, it’s useful to sit down and think broadly about what wealth really means to us. More time? More freedom? Less anxiety? It’s only by knowing what we{' '}
          <span className="italic">really</span> want that we can start on the path to getting it.
        </p>,
      ],
    },
  },
  {
    section: 2,
    content: {
      title: <h3>Step Two: Separate Needs and Wants</h3>,
      paragraph: [
        <p>
          According to Maslow’s pyramid, there’s a pecking order when it comes to human needs. The broadest category-- at the bottom of the pyramid-- is fairly straightforward:
          food, clothing, and shelter are fundamental requirements for life. But at the higher levels of the pyramid, it becomes more and more difficult to distinguish complex
          human needs from <span className="italic">wants</span>. Yes, it would be nice to have that fancy new phone. But is it also a key component in the quest for
          self-actualization?
        </p>,
        <ImageSection selectedImage={pyramid} mobileImage={pyramidMobile} classname={'pyramid'} />,
        <p>
          Common sense says no. But in a moment in which social media is constantly blasting us with evidence of just how much everyone else has, it’s easy for the line to get
          blurry. According to Misty Lynch, Head of Financial Planning at John Hancock, it’s important to avoid letting the reality we see in our online feeds muddy our priorities.
          On social media, she says, “You’re seeing the highlights of everybody’s lives. It’s really hard not to feel something when they see somebody else [living it up]. But
          we’re not seeing the credit card bill after.”
        </p>,
        <p>
          Lynch stresses that it’s not a bad thing to strive for things—even material ones. But it’s important to check in with yourself regularly to see what you really need
          compared to what you really want. “Look internally and ask what you want and how to use your money in a smarter way to get those things that actually matter to you,
          rather than what you think will impress others.”
        </p>,
        <p>
          As you start or{' '}
          <a
            href="https://www.johnhancock.com/articles/budget-debunking-rule.html?cid=US_JH_BR_DI_CNN_ContentPartnership_Advice_GA_CS_LK_DP_SP_DE_00_AWCTR_1x1_20191119_BudgetingPractice_PsychologyofWealth;260654825"
            target="_blank"
            rel="noopener noreferrer"
          >
            refine your budgeting practice
          </a>
          , try to think about the way you allocate your money in terms of needs vs. wants, always keeping in mind the goal you set earlier.
        </p>,
      ],
    },
  },
  {
    section: 3,
    content: {
      title: (
        <h3>
          Step Three: Untangle Your Relationship <br /> with Money
        </h3>
      ),
      paragraph: [
        <p>
          Now comes the trickiest part. As with our personal relationships, each of us has our own challenges in our relationship with money that we need to understand and
          troubleshoot around.
        </p>,
        <p>
          According to psychologists, some of the most common relationship types when it comes to money are the <span className="bold">avoider</span>, the{' '}
          <span className="bold">hoarder</span>, and the <span className="bold">big spender</span>.{' '}
          <a
            href="https://www.johnhancock.com/articles/financial-attitudes.html?cid=US_JH_BR_DI_CNN_ContentPartnership_Advice_GA_CS_LK_DP_SP_DE_00_AWCTR_1x1_20191119_DefineProfile_PsychologyofWealth;260653838"
            target="_blank"
            rel="noopener noreferrer"
          >
            To help define your own profile
          </a>
          , try looking for your own trouble spots: for instance, if you’re an avoider, you’ll recognize yourself as someone who never looks at your bank statements, doesn’t know
          what is invested on your behalf, and doesn’t make a budget. A hoarder, on the other hand, might consistently default to what Lynch calls, “scarcity mode,” holding onto
          money just for the sake of having it.
        </p>,
        <p>
          While it might be a little jarring to recognize yourself in those types, understanding{' '}
          <a
            href="https://www.johnhancock.com/articles/10-things-a-financial-therapist-wants-you-to-know.html?cid=US_JH_BR_DI_CNN_ContentPartnership_Advice_GA_CS_LK_DP_SP_DE_00_AWCTR_1x1_20191119_RelateMoney_PsychologyofWealth;260653844"
            target="_blank"
            rel="noopener noreferrer"
          >
            the way you relate to your money
          </a>{' '}
          is the key to making that relationship healthier.
        </p>,
      ],
    },
  },
  {
    section: 4,
    content: {
      title: <h3>Step Four: Make the Connection</h3>,
      paragraph: [
        <p>
          Enough with all that introspection. While it’s important to do some soul searching as you set off on your quest, you don’t need to go it alone. The next step is{' '}
          <a
            href="https://www.johnhancock.com/individual/products-and-services/planning-and-advice.html?cid=US_JH_BR_DI_CNN_ContentPartnership_Advice_GA_CS_LK_DP_SP_DE_00_AWCTR_1x1_20191119_TrustedProfessional_PsychologyofWealth;260654819"
            target="_blank"
            rel="noopener noreferrer"
          >
            meeting with a trusted professional
          </a>{' '}
          who can help you name and claim the things that matter to you. They can give you a practical, actionable plan for putting your money to work for you on that journey—no
          matter how much of it you have.
        </p>,
        <p>
          Being upfront and honest is crucial from the start. When Lynch meets with clients, the big picture is the name of the game. “I take a look at somebody's personal
          financial situation, their job, what they care about, their family, and try to see where they might feel better-- where there might be blind spots,” she says.
        </p>,
        <p>
          Remember Maslow’s pyramid? At the top is “self-actualization.” That achievement, rather than any dollar figure, is where you should consider yourself headed. To get
          there, you’ll need a plan—and{' '}
          <a
            href="https://www.johnhancock.com/individual/products-and-services/planning-and-advice.html?cid=US_JH_BR_DI_CNN_ContentPartnership_Advice_GA_CS_LK_DP_SP_DE_00_AWCTR_1x1_20191119_JohnHancock_PsychologyofWealth;261054759"
            target="_blank"
            rel="noopener noreferrer"
          >
            John Hancock can help you make one
          </a>
          .
        </p>,
      ],
    },
  },
];

export default copy;
